/**
 * Client-side persistence for the standalone POC — incidents survive a
 * reload, and (since the multi-incident dashboard epic) more than one can
 * be open at once. This is the localStorage adapter behind a small
 * repository seam; an Azure Table adapter (via the API) slots in here when
 * we wire deployment.
 *
 * Each incident's record carries its own `audit`/`interactions` alongside
 * the `Incident` itself — before this, those two lived only in ephemeral
 * `useState` in `store.tsx` and were silently lost on every reload or
 * incident-identity change. That was invisible in a single-incident app
 * (there was only ever one incident to lose state for, and reloading
 * mid-job was rare); it becomes a real regression once switching between
 * several open incidents is a normal action, so it's fixed here rather
 * than carried forward.
 */
import { migrateIncident, type Incident, type AuditEvent, type InsightStatus } from '@duty/shared';

export interface Interaction {
  status: InsightStatus;
  dismissReason?: string;
}

export interface PersistedIncidentRecord {
  incident: Incident;
  audit: AuditEvent[];
  interactions: Record<string, Interaction>;
}

export interface PersistedStore {
  recordsById: Record<string, PersistedIncidentRecord>;
  activeId: string | null;
}

const STORE_KEY = 'duty.incidents.v1';
/** Pre-multi-incident single key — read once for migration, never written to again. */
const LEGACY_KEY = 'duty.incident.v1';

function emptyStore(): PersistedStore {
  return { recordsById: {}, activeId: null };
}

/** Best-effort persist of the whole store (every open incident's record + which one is active). */
export function saveStore(store: PersistedStore): void {
  try {
    window.localStorage.setItem(STORE_KEY, JSON.stringify(store));
  } catch {
    // Storage may be unavailable (private mode); the app still works in-memory.
  }
}

function isValidRecord(r: unknown): r is PersistedIncidentRecord {
  if (typeof r !== 'object' || r === null) return false;
  const rec = r as Record<string, unknown>;
  const incident = rec.incident as Partial<Incident> | undefined;
  return !!incident && typeof incident === 'object' && !!incident.id && !!incident.itasc;
}

/**
 * Load the multi-incident store. If the new keyed store doesn't exist yet
 * but the old single-incident key does, migrate it into a one-entry store
 * (empty audit/interactions, since neither was ever durable before this)
 * — the legacy key is left in place, never deleted, so nothing is silently
 * discarded if this migration path has a bug.
 */
export function loadStore(): PersistedStore {
  try {
    const raw = window.localStorage.getItem(STORE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<PersistedStore>;
      const recordsById: Record<string, PersistedIncidentRecord> = {};
      for (const [id, rec] of Object.entries(parsed.recordsById ?? {})) {
        if (!isValidRecord(rec)) continue;
        recordsById[id] = {
          incident: migrateIncident(rec.incident as Partial<Incident> & { schemaVersion?: number }),
          audit: Array.isArray(rec.audit) ? rec.audit : [],
          interactions: rec.interactions && typeof rec.interactions === 'object' ? rec.interactions : {},
        };
      }
      const persistedActiveId = typeof parsed.activeId === 'string' && recordsById[parsed.activeId] ? parsed.activeId : null;
      // Auto-resume straight into the workspace only when there's exactly one open
      // incident (matches the pre-dashboard single-incident behaviour exactly — reload
      // and you're back where you were). With more than one open, land on the dashboard
      // instead of silently resuming whichever happened to be active when the tab
      // closed — showing the portfolio first is the whole point once there's a choice
      // to make. This only affects the load-time value; switching incidents mid-session
      // still sets `activeId` directly and is unaffected.
      const activeId = Object.keys(recordsById).length > 1 ? null : persistedActiveId;
      return { recordsById, activeId };
    }

    // Migration: no keyed store yet — check for a pre-existing single incident.
    const legacyRaw = window.localStorage.getItem(LEGACY_KEY);
    if (legacyRaw) {
      const parsed = JSON.parse(legacyRaw) as Partial<Incident> & { schemaVersion?: number };
      if (parsed && typeof parsed === 'object' && parsed.id && parsed.itasc) {
        const incident = migrateIncident(parsed);
        return { recordsById: { [incident.id]: { incident, audit: [], interactions: {} } }, activeId: incident.id };
      }
    }
    return emptyStore();
  } catch {
    return emptyStore();
  }
}
